import { DFPModal } from "@/components/modals/dfp-modal";
import { CSVExporter } from "@/components/pure/csv-expoter/csv-exporter";
import { csvFieldTransformFnMap } from "@/components/pure/csv-expoter/field-transforms";
import { CORE_URL } from "@/constants/api";
import { VITE_CORE_API_URL } from "@/constants/env";
import { auth } from "@/services/auth-service";
import { selfOrganizationAssetTypes } from "@/services/organization-service";
import { useUserStore } from "@/stores";
import { isUnknownValue } from "@/utils/isUnknownValue";
import { Button, Select, message } from "antd";
import { Dispatch, FC, useCallback, useEffect, useState } from "react";
import { State } from "./AssetsTable";

type Action = { type: "SET_SHOW_SC_EXPORT"; payload: boolean };

interface SCExportDialogProps {
	state: State;
	dispatch: Dispatch<Action>;
	browserLocationId: string | undefined;
}

type CategoryOption = {
	label: string;
	value: string;
};

export const SCExportDialog: FC<SCExportDialogProps> = ({
	state,
	dispatch,
	browserLocationId,
}) => {
	const { searchVal, pageNum, pageSize, sortBy, sortOrder, dateRange } = state;
	const [loading, setLoading] = useState<boolean>(false);
	const [chosenCategory, setChosenCategory] = useState<string | null>(null);
	const [categories, setCategories] = useState<CategoryOption[]>([]);
	const [csvData, setCsvData] = useState<any[] | null>(null);

	const loadAssetTypesData = async () => {
		try {
			// Include all asset types (including deprecated) for export mappings
			// Note: selfOrganizationAssetTypes returns an array of strings (names), not objects
			const res: string[] = await selfOrganizationAssetTypes(true);

			if (!res || res.length === 0) {
				console.warn("No asset types returned from API");
				setCategories([{ label: "All Asset Types", value: "ALL" }]);
				return;
			}

			const options = res.map((typeName: string) => ({
				label: typeName,
				value: typeName,
			}));

			// Add "All Asset Types" option at the beginning
			const allOptions = [
				{ label: "All Asset Types", value: "ALL" },
				...options,
			];

			setCategories(allOptions);
		} catch (error) {
			console.error("Failed to load asset types", error);
			setCategories([{ label: "All Asset Types", value: "ALL" }]);
		}
	};

	// Fetch asset types when the modal opens
	useEffect(() => {
		if (state.showSCExport && categories.length === 0) {
			loadAssetTypesData();
		}
	}, [state.showSCExport]);

	const handleExport = useCallback(async () => {
		message.info("Exporting...");
		setLoading(true);

		try {
			// Get organization ID
			const user = useUserStore.getState().user;
			const organizationId = user?.organization?.id;

			if (!organizationId) {
				throw new Error("Organization ID not found");
			}

			// Fetch all assets for the organization
			const url = `${VITE_CORE_API_URL}${CORE_URL}/asset/organization/${organizationId}`;
			const response = await fetch(url, {
				method: "GET",
				headers: {
					Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
					"Content-Type": "application/json",
				},
			});

			if (!response.ok) {
				throw new Error(`Failed to fetch assets (${response.status})`);
			}

			const result = await response.json();
			let allAssets = result.success ? result.data : result;

			if (!Array.isArray(allAssets) || allAssets.length === 0) {
				message.error("No assets found to export");
				return;
			}

			// Apply filters similar to fetchData.tsx

			// Filter by chosen asset type category (skip if "ALL" is selected)
			if (chosenCategory && chosenCategory !== "ALL") {
				allAssets = allAssets.filter(
					(asset: any) => asset.assetType?.name === chosenCategory,
				);
			}

			// Apply search filter if present
			if (searchVal) {
				const searchLower = searchVal.toLowerCase();
				allAssets = allAssets.filter((asset: any) => {
					return (
						asset.assetTag?.toLowerCase().includes(searchLower) ||
						asset.assetType?.name?.toLowerCase().includes(searchLower) ||
						asset.manufacturer?.name?.toLowerCase().includes(searchLower) ||
						asset.modelNumber?.toLowerCase().includes(searchLower) ||
						asset.serialNumber?.toLowerCase().includes(searchLower) ||
						asset.location?.name?.toLowerCase().includes(searchLower) ||
						asset.location?.address1?.toLowerCase().includes(searchLower)
					);
				});
			}

			// Apply location filter if present
			if (browserLocationId) {
				const locationId = parseInt(browserLocationId);
				allAssets = allAssets.filter(
					(asset: any) => asset.locationId === locationId,
				);
			}

			// Apply date range filter if present
			if (dateRange[0] || dateRange[1]) {
				allAssets = allAssets.filter((asset: any) => {
					const assetDate = new Date(asset.createdTimestamp);
					const startDate = dateRange[0];
					const endDate = dateRange[1];

					if (startDate && assetDate < startDate) return false;
					if (endDate && assetDate > endDate) return false;
					return true;
				});
			}

			if (allAssets.length === 0) {
				message.error("No assets found matching the selected criteria");
				return;
			}

			// Clean up unknown values and convert uniqueFields to JSON string
			const filteredUnknownResults = allAssets.map((asset: any) => {
				const newAsset = { ...asset };
				const { modelNumber, manufacturer, uniqueFields } = newAsset;

				if (modelNumber && isUnknownValue(modelNumber)) {
					newAsset.modelNumber = "N/A";
				}
				if (manufacturer?.name && isUnknownValue(manufacturer.name)) {
					newAsset.manufacturer = { ...manufacturer, name: "N/A" };
				}

				// Convert uniqueFields object to JSON string to prevent it from being flattened
				// This ensures it stays as a single CSV column
				if (uniqueFields && typeof uniqueFields === "object") {
					newAsset.uniqueFields = JSON.stringify(uniqueFields);
				}

				return newAsset;
			});

			setCsvData(filteredUnknownResults);
			message.success("Export successful");
		} catch (error) {
			console.error("Error exporting assets:", error);
			message.error("Failed to export assets. Please try again.");
		} finally {
			setLoading(false);
		}
	}, [browserLocationId, chosenCategory, dateRange, searchVal]);

	const toggleSCExport = () => {
		dispatch({ type: "SET_SHOW_SC_EXPORT", payload: !state.showSCExport });
	};

	// Reset csvData after export to prevent multiple downloads
	const handleExportComplete = () => {
		setCsvData(null);
	};

	// Define the specific fields to export in order
	const exportFields = [
		"id",
		"assetTag",
		"tempAlertTag",
		"createdTimestamp",
		"location.name",
		"location.storeNumber",
		"location.address1",
		"location.city",
		"location.state",
		"location.zip",
		"assetType.name",
		"manufacturer.name",
		"modelNumber",
		"serialNumber",
		"manufacturerDate",
		"dateOfBirth",
		"condition",
		"powerSource",
		"electricalDetails.amps",
		"electricalDetails.voltage",
		"electricalDetails.wattage",
		"refrigerationDetails.refrigerantType",
		"refrigerationDetails.refrigerantCharge",
		"refrigerationDetails.refrigerantChargeMeasurementUnit",
		"equipmentArea.name",
		"equipmentSubArea.name",
		"source",
		"generalNotes",
		"uniqueFields",
		"photos[0].type",
		"photos[0].url",
		"photos[1].type",
		"photos[1].url",
		"photos[2].type",
		"photos[2].url",
	];

	// Define custom column header mappings
	const customHeaderMap: { [key: string]: string } = {
		id: "FAI ID",
		assetTag: "ASSET TAG",
		tempAlertTag: "TEMP ALERT TAG",
		createdTimestamp: "CREATED",
		"location.name": "LOCATION",
		"location.storeNumber": "STORE NUMBER",
		"location.address1": "ADDRESS",
		"location.city": "CITY",
		"location.state": "STATE",
		"location.zip": "ZIP",
		"assetType.name": "ASSET TYPE",
		"manufacturer.name": "MANUFACTURER",
		modelNumber: "MODEL",
		serialNumber: "SERIAL",
		manufacturerDate: "MANUFACTURER DATE",
		dateOfBirth: "DATE OF BIRTH",
		condition: "CONDITION",
		powerSource: "POWER SOURCE",
		"electricalDetails.amps": "AMPS",
		"electricalDetails.voltage": "VOLTAGE",
		"electricalDetails.wattage": "WATTAGE",
		"refrigerationDetails.refrigerantType": "REFRIGERANT TYPE",
		"refrigerationDetails.refrigerantCharge": "REFRIGERANT CHARGE",
		"refrigerationDetails.refrigerantChargeMeasurementUnit": "REFRIGERANT UNIT",
		"equipmentArea.name": "AREA",
		"equipmentSubArea.name": "SUB AREA",
		source: "SOURCE",
		generalNotes: "NOTES",
		uniqueFields: "UNIQUE FIELDS",
		"photos[0].type": "PHOTO TYPE (1)",
		"photos[0].url": "PHOTO URL (1)",
		"photos[1].type": "PHOTO TYPE (2)",
		"photos[1].url": "PHOTO URL (2)",
		"photos[2].type": "PHOTO TYPE (3)",
		"photos[2].url": "PHOTO URL (3)",
	};

	return (
		<>
			<DFPModal
				title="Export Assets to CSV"
				open={state.showSCExport}
				onCancel={toggleSCExport}
				confirmLoading={loading}
				footer={[
					<Button key="cancel" onClick={toggleSCExport}>
						Cancel
					</Button>,
					<Button
						key="export"
						type="primary"
						disabled={!chosenCategory}
						loading={loading}
						onClick={handleExport}
					>
						Export
					</Button>,
				]}
			>
				<Select
					style={{ width: "100%" }}
					placeholder="Select asset type to export (or choose 'All Asset Types')"
					onChange={(value) => {
						setChosenCategory(value);
					}}
					value={chosenCategory}
				>
					{categories.map((category) => (
						<Select.Option key={category.value} value={category.value}>
							{category.label}
						</Select.Option>
					))}
				</Select>
			</DFPModal>

			{/* Conditionally render CSVExporter when csvData is available */}
			{csvData && csvData.length > 0 && (
				<CSVExporter
					data={csvData}
					filename="exported-data.csv"
					autoDownload
					fields={exportFields}
					fieldTransforms={csvFieldTransformFnMap}
					customHeaderMap={customHeaderMap}
					onComplete={handleExportComplete}
				/>
			)}
		</>
	);
};
